import React, { useEffect, useState, useContext } from "react";
import "./AddVenue.css";
import { useLocation, useNavigate } from "react-router-dom";
import CurrentStep from "./CurrentStep";
import { toast, ToastContainer } from "react-toastify";
import Loading from "../../components/Loader";
import Success from "./Success";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import { useTranslation } from "react-i18next";
import AuthContext from "../../contexts/AuthProvider";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const StepFour = ({ files, update, status, setStatus }) => {
  const { t } = useTranslation("addvenue");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  // Load state from localStorage or use default values
  const [pricing, setPricing] = useState(() => {
    const savedData = localStorage.getItem("pricingData");
    return savedData
      ? JSON.parse(savedData)
      : {
        hourlyRate: "25",
        isHourlyEnabled: false,
        dailyRate: "500",
        isDailyEnabled: true,
        perGuest: false,
        minCapacity: "",
      };
  });
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  // Helper function to convert local time to UTC
  const convertToUTC = (time) => {
    // Add validation to prevent the error
    if (!time || typeof time !== "string") {
      console.warn("Invalid time value provided to convertToUTC:", time);
      return "09:00"; // Default fallback time
    }

    const timeParts = time.split(":");
    if (timeParts.length !== 2) {
      console.warn("Invalid time format provided to convertToUTC:", time);
      return "09:00"; // Default fallback time
    }

    const [hours, minutes] = timeParts.map(Number);

    // Validate hours and minutes
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      console.warn("Invalid time values provided to convertToUTC:", time);
      return "09:00"; // Default fallback time
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0); // Set local time
    return `${date.getUTCHours().toString().padStart(2, "0")}:${date
      .getUTCMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  // Save to localStorage whenever pricing state changes
  useEffect(() => {
    localStorage.setItem("pricingData", JSON.stringify(pricing));
  }, [pricing]);

  // Handle pricing type change (hourly vs daily)
  const handlePricingTypeChange = (e) => {
    const selectedType = e.target.value;
    const isHourly = selectedType === "hourly";

    setPricing((prev) => ({
      ...prev,
      isHourlyEnabled: isHourly,
      isDailyEnabled: !isHourly,
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Allow empty string for easier editing
    if (value === "") {
      setPricing((prev) => ({
        ...prev,
        [name]: "",
      }));
      return;
    }

    // Allow only valid decimal input patterns
    // This regex allows: numbers, single decimal point, partial decimals like "12." or ".5"
    const decimalRegex = /^(\d*\.?\d*|\.\d*)$/;

    if (decimalRegex.test(value)) {
      // Check if it's a valid number for range validation
      const numValue = parseFloat(value);

      // Allow the input if it's a valid partial input or within range
      if (isNaN(numValue) || (numValue >= 0 && numValue < 1000000)) {
        setPricing((prev) => ({
          ...prev,
          [name]: value, // Store as string to preserve decimal point during typing
        }));
      }
    }
  };



  // Handle per guest toggle
  const handlePerGuestToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPricing((prev) => ({
      ...prev,
      perGuest: !prev.perGuest,
    }));
  };

  // Handle minimum capacity input
  const handleMinCapacityChange = (e) => {
    const { value } = e.target;

    // Allow empty string for easier editing
    if (value === "") {
      setPricing((prev) => ({
        ...prev,
        minCapacity: "",
      }));
      return;
    }

    // Allow only numbers
    const numberRegex = /^\d*$/;

    if (numberRegex.test(value)) {
      const numValue = parseInt(value);

      // Allow the input if it's within reasonable range
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 10000) {
        setPricing((prev) => ({
          ...prev,
          minCapacity: value,
        }));
      }
    }
  };

  // Handle navigation to the next step
  const handleNavigateWithParams = (step) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("step", step);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const initialState = {
    files: [], // Array to hold multiple files
    previews: [], // Array to hold multiple preview URLs
    name: "",
    brand: "",
    year: null,
    capacity: null,
    guest_rooms: null,
    size: null,
    venue_type_id: null,
    country: "",
    city: "",
    latitude: null,
    longitude: null,
    terms: null,
  };

  const getState = () => {
    const savedState = localStorage.getItem("appState"); // Retrieve saved state
    if (savedState) {
      try {
        return JSON.parse(savedState); // Parse the saved state
      } catch (error) {
        console.error("Error parsing saved state:", error);
        return initialState; // Fallback to initial state
      }
    }
    return initialState; // Use initial state if no saved state exists
  };

  // Load state from localStorage or use initial state
  const state = getState();

  // Initial state for working hours
  const initialWorkingHours = {
    Sunday: { enabled: false, from: "9:00", to: "17:00" },
    Monday: { enabled: false, from: "9:00", to: "17:00" },
    Tuesday: { enabled: false, from: "9:00", to: "17:00" },
    Wednesday: { enabled: false, from: "9:00", to: "17:00" },
    Thursday: { enabled: false, from: "9:00", to: "17:00" },
    Friday: { enabled: false, from: "9:00", to: "17:00" },
    Saturday: { enabled: false, from: "9:00", to: "17:00" },
  };

  const getWorkingHours = () => {
    const savedState = localStorage.getItem("workingHours");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);

        // Validate the parsed state structure
        const daysOfWeek = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];
        const isValidStructure = daysOfWeek.every((day) => {
          const dayData = parsedState[day];
          return (
            dayData &&
            typeof dayData.enabled === "boolean" &&
            typeof dayData.from === "string" &&
            typeof dayData.to === "string" &&
            dayData.from.includes(":") &&
            dayData.to.includes(":")
          );
        });

        if (isValidStructure) {
          return parsedState;
        } else {
          console.warn(
            "Invalid working hours structure in localStorage, using defaults"
          );
          // Clear the corrupted data
          localStorage.removeItem("workingHours");
          return initialWorkingHours;
        }
      } catch (error) {
        console.error("Error parsing working hours from localStorage:", error);
        // Clear the corrupted data
        localStorage.removeItem("workingHours");
        return initialWorkingHours;
      }
    }
    return initialWorkingHours;
  };

  // Load state from localStorage or use initial state
  const workingHours = getWorkingHours();

  const getSelectedAmenities = () => {
    try {
      const savedAmenities = localStorage.getItem("selectedAmenities");
      return savedAmenities ? JSON.parse(savedAmenities) : [];
    } catch (error) {
      console.error("Failed to load selected amenities:", error);
      return [];
    }
  };

  const getSelectedEventTypes = () => {
    try {
      const saved = localStorage.getItem("selectedEventTypes");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Failed to load selected event types:", error);
      return [];
    }
  };

  const getSelectedInclusionIds = () => {
    try {
      const saved = localStorage.getItem("selectedInclusionIds");
      const parsed = saved ? JSON.parse(saved) : [];
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .map((item) => (typeof item === "number" ? item : parseInt(item, 10)))
        .filter((id) => !isNaN(id));
    } catch (error) {
      console.error("Failed to load selected inclusions:", error);
      return [];
    }
  };

  const getSelectedInclusions = () => {
    try {
      const saved = localStorage.getItem("selectedInclusions");
      const parsed = saved ? JSON.parse(saved) : [];
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .map((inclusionItem) => {
          if (
            !inclusionItem ||
            inclusionItem.id === undefined ||
            inclusionItem.name === undefined
          ) {
            return null;
          }
          return {
            id: String(inclusionItem.id),
            name: String(inclusionItem.name),
          };
        })
        .filter(Boolean);
    } catch (error) {
      console.error("Failed to load inclusion objects:", error);
      return [];
    }
  };

  // Retrieve selected amenities from localStorage or default to an empty array
  const selectedAmenities = getSelectedAmenities();
  const selectedEventTypes = getSelectedEventTypes();
  const [inclusionsList, setInclusionsList] = useState([]);
  const [selectedInclusionIds, setSelectedInclusionIds] = useState(() =>
    getSelectedInclusionIds()
  );
  const [selectedInclusionItems, setSelectedInclusionItems] = useState(() =>
    getSelectedInclusions()
  );

  const searchParams = new URLSearchParams(location.search);
  const [editVenue, setEditVenue] = useState(
    searchParams.get("edit") === "true"
  );

  // Helper function to convert day names to numbers (0-6, where 0 is Sunday)
  const getDayNumber = (dayName) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days.indexOf(dayName);
  };
  useEffect(() => {
    if (status === "Success") {
      // Set a timeout to reset the status after 3 seconds
      const timer = setTimeout(() => {
        setStatus(""); // Reset status to empty string
      }, 2000); // 3000 milliseconds = 3 seconds

      // Cleanup the timer if the component unmounts or status changes
      return () => clearTimeout(timer);
    }
  }, [status]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${baseUrl}/api/v1/inclusions`, {
      credentials: "include",
      headers: { Accept: "application/json", lang: getLangFromLocalStorage() },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch inclusions");
        return res.json();
      })
      .then((data) => setInclusionsList(data.data || data || []))
      .catch((error) => {
        console.error(error);
        setInclusionsList([]);
      });
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem(
      "selectedInclusionIds",
      JSON.stringify(selectedInclusionIds)
    );
  }, [selectedInclusionIds]);

  useEffect(() => {
    const cachedInclusionsById = new Map(
      getSelectedInclusions().map((inclusionItem) => [
        String(inclusionItem.id),
        inclusionItem,
      ])
    );

    const selectedItems = selectedInclusionIds
      .map((selectedId) => {
        const matchedInclusion = inclusionsList.find(
          (inclusion) => Number(inclusion.id) === Number(selectedId)
        );
        if (matchedInclusion) {
          return {
            id: String(matchedInclusion.id),
            name:
              matchedInclusion.name ||
              matchedInclusion.name_ar ||
              String(matchedInclusion.id),
          };
        }

        return (
          cachedInclusionsById.get(String(selectedId)) || {
            id: String(selectedId),
            name: String(selectedId),
          }
        );
      })
      .filter(Boolean);

    setSelectedInclusionItems(selectedItems);
    localStorage.setItem("selectedInclusions", JSON.stringify(selectedItems));
  }, [selectedInclusionIds, inclusionsList]);

  const [cities, setCities] = useState([]);

  // Fetch cities on component mount
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${baseUrl}/api/v1/cities`, {
      credentials: "include",
      headers: { Accept: "application/json", lang: getLangFromLocalStorage() },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch cities");
        return res.json();
      })
      .then((data) => setCities(data.data || data))
      .catch(console.error);
  }, [isAuthenticated]);

  // Helper function to get location name
  const getLocationName = () => {
    const savedLocationName = localStorage.getItem("locationName");
    if (savedLocationName) return savedLocationName;

    // Fallback to city name if location name not set
    const cityId = state.city;
    const cityName = cities.find((city) => city.id === cityId)?.name || "";
    return cityName;
  };

  const [adding, setAdding] = useState(false);
  const createVenue = async () => {
    if (adding) return;
    const loadingToast = toast.loading(t("stepFour.processing"));
    setAdding(true);
    try {
      if (!isAuthenticated) {
        throw new Error("No authentication token found. Please log in again.");
      }

      // Transform workingHours into the required format
      const workingDays = Object.entries(workingHours)
        .filter(([_, value]) => {
          // More thorough validation
          return (
            value &&
            value.enabled === true &&
            value.from &&
            value.to &&
            typeof value.from === "string" &&
            typeof value.to === "string" &&
            value.from.includes(":") &&
            value.to.includes(":")
          );
        })
        .map(([day, value]) => {
          try {
            return {
              day: getDayNumber(day),
              from: convertToUTC(value.from),
              to: convertToUTC(value.to),
            };
          } catch (error) {
            console.error(`Error processing working hours for ${day}:`, error);
            // Return a default working day if there's an error
            return {
              day: getDayNumber(day),
              from: convertToUTC("09:00"),
              to: convertToUTC("17:00"),
            };
          }
        });

      const blockedDates = JSON.parse(localStorage.getItem("unavailableDates")) || [];
      const formData = new FormData();
      formData.append("name", state.name);
      formData.append("location", getLocationName());
      formData.append("size", state.size);
      formData.append("capacity", state.capacity);
      formData.append("venue_type_id", state.venue_type_id);
      if (state.guest_rooms && Number(state.guest_rooms) !== 0)
        formData.append("guest_rooms", state.guest_rooms);
      formData.append("build", state.year || 0);
      formData.append("brand", state.brand || "");
      formData.append("country", state.country || "");
      formData.append("city", state.city || "");
      formData.append("latitude", state.latitude || "");
      formData.append("longitude", state.longitude || "");
      if (pricing.isDailyEnabled)
        formData.append("daily_rate", parseFloat(pricing.dailyRate).toFixed(2));
      else
        formData.append(
          "hourly_rate",
          parseFloat(pricing.hourlyRate).toFixed(2)
        );

      // Add new pricing fields
      formData.append("per_attendees", pricing.perGuest ? true : false);
      if (pricing.minCapacity && pricing.minCapacity !== "") {
        formData.append("min_capacity", parseInt(pricing.minCapacity));
      }

      formData.append("working_days", JSON.stringify(workingDays));
      formData.append("blocked_days", JSON.stringify(blockedDates));
      formData.append("amenities", JSON.stringify(selectedAmenities));
      formData.append("event_type_ids", JSON.stringify(selectedEventTypes));
      formData.append(
        "venue_inclusion_ids",
        JSON.stringify(selectedInclusionIds)
      );
      // formData.append("images", state.files);
      let convertedFiles = files;
      console.log(state.previews);
      console.log(convertedFiles);
      if (files.length !== state.previews.length) {
        // Retrieve files from sessionStorage with error handling
        let storedFiles = [];
        try {
          const filesData = sessionStorage.getItem("files");
          if (filesData) {
            storedFiles = JSON.parse(filesData) || [];
          }
        } catch (error) {
          console.error(
            "Error parsing stored files from sessionStorage:",
            error
          );
          // Clear corrupted data
          sessionStorage.removeItem("files");
          storedFiles = [];
        }
        if (storedFiles.length > 0) {
          convertedFiles = storedFiles
            .filter((fileData) => {
              // Validate fileData structure
              return (
                fileData &&
                fileData.base64 &&
                typeof fileData.base64 === "string" &&
                fileData.base64.includes(",") &&
                fileData.type &&
                fileData.name
              );
            })
            .map((fileData) => {
              try {
                const base64Data = fileData.base64.split(",")[1];
                if (!base64Data) {
                  throw new Error("Invalid base64 data");
                }
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: fileData.type });
                return new File([blob], fileData.name, { type: fileData.type });
              } catch (error) {
                console.error("Error converting stored file:", error, fileData);
                return null;
              }
            })
            .filter((file) => file !== null); // Remove any failed conversions
        }
      }
      console.log(convertedFiles);
      convertedFiles.forEach((image) => {
        formData.append("images", image);
      });

      formData.append("terms_and_conditions", state.terms);

      // API request POST

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/createVenue`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            lang: getLangFromLocalStorage(),
          },
          body: formData,
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized access. Please log in again.");
        }
        const error = await response.json();
        const [key, value] = error?.errors
          ? Object.entries(error?.errors[0])[0]
          : Object.entries({ "": "" });
        throw new Error(
          error?.error ||
          (error?.errors && `${key}: ${value}`) ||
          "An unknown error occurred."
        );
      }

      // Parse the response to get the venue ID
      const responseData = await response.json();
      const venueId = responseData.venue_id;

      localStorage.removeItem("appState");
      localStorage.removeItem("selectedAmenities");
      localStorage.removeItem("selectedEventTypes");
      localStorage.removeItem("selectedInclusionIds");
      localStorage.removeItem("selectedInclusions");
      localStorage.removeItem("workingHours");
      localStorage.removeItem("unavailableDates");
      localStorage.removeItem("pricingData");
      sessionStorage.removeItem("files");

      // Success handling
      // {"message":"تم إنشاء المكان والمواعيد بنجاح","venue_id":42}
      toast.update(loadingToast, {
        render: t("stepFour.success"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      setAdding(false);

      // Navigate using the returned venue ID
      if (venueId) {
        localStorage.setItem("venueId", venueId);
        navigate(`/venues/${venueId}`);

      } else {
        navigate("/venues");
      }
    } catch (error) {
      console.error(error);
      setAdding(false);
      toast.update(loadingToast, {
        render: `${error.message || error}`,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  useEffect(() => {
    setEditVenue(searchParams.get("edit") === "true");
  }, [searchParams]);

  const [loading, setLoading] = useState(editVenue);
  const [error, setError] = useState(null);
  const id = localStorage.getItem("venueId"); // Get the 'query' param

  useEffect(() => {
    const fetchVenues = async () => {
      if (!isAuthenticated) {
        setError(new Error("No authentication token found. Please log in."));
        setLoading(false);
        navigate("/");
        return;
      }
      try {
        //GET
        const venueResponse = await fetch(
          `${baseUrl}/api/v1/attnd-venue/venues/${id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              lang: getLangFromLocalStorage(),
            },
          }
        );

        if (!venueResponse.ok) {
          if (venueResponse.status === 401) {
            navigate("/login");
            throw new Error("Unauthorized access. Please log in again.");
          } else if (venueResponse.status === 404) {
            alert("404 Not found.");
            navigate(-1);
            throw new Error("404 Not found.");
          } else {
            throw new Error(`An error occurred: ${venueResponse.statusText}`);
          }
        }

        const venue = await venueResponse.json();
        const venueData = venue.venue_details;
        const data = {
          hourlyRate: (Number(venueData.hourly_rate) || 25).toString(),
          isHourlyEnabled: Number(venueData.hourly_rate) !== 0,
          dailyRate: (Number(venueData.daily_rate) || 500).toString(),
          isDailyEnabled: Number(venueData.daily_rate) !== 0,
          perGuest:
            venueData.per_attendees === true ||
            venueData.per_attendees === "true",
          minCapacity: venueData.min_capacity
            ? venueData.min_capacity.toString()
            : "",
        };
        setPricing(data);
        let parsedInclusions = [];
        try {
          if (Array.isArray(venueData.venue_inclusions)) {
            parsedInclusions = venueData.venue_inclusions;
          } else if (
            typeof venueData.venue_inclusions === "string" &&
            venueData.venue_inclusions.trim() !== ""
          ) {
            const parsed = JSON.parse(venueData.venue_inclusions);
            parsedInclusions = Array.isArray(parsed) ? parsed : [];
          }
        } catch (parseError) {
          console.error("Failed to parse venue inclusions:", parseError);
        }

        const inclusionIds = parsedInclusions
          .map((inclusionItem) =>
            inclusionItem && inclusionItem.id !== undefined
              ? Number(inclusionItem.id)
              : NaN
          )
          .filter((idValue) => !isNaN(idValue));
        setSelectedInclusionIds(inclusionIds);
        setSelectedInclusionItems(
          parsedInclusions
            .map((inclusionItem) => {
              if (!inclusionItem || inclusionItem.id === undefined) {
                return null;
              }
              return {
                id: String(inclusionItem.id),
                name:
                  inclusionItem.name !== undefined
                    ? String(inclusionItem.name)
                    : String(inclusionItem.id),
              };
            })
            .filter(Boolean)
        );
        setLoading(false);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
        console.log(selectedAmenities);
      }
    };

    if (editVenue) fetchVenues();
  }, [baseUrl]);

  if (loading) {
    return (
      <div
        style={{
          height: "calc(100vh - 400px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="venue-venues-container">
        Error fetching venue: {error.message}
      </div>
    );
  }

  return (
    <div
      className={"steps pricing-page"}
      style={editVenue ? { margin: "0" } : {}}
    >
      <CurrentStep step={5} />
      <div
        className={"Price figma-pricing-container"}
        style={editVenue ? { margin: "0", padding: "0" } : {}}
      >
        {/* Per Guest Option Header - TOP of form */}
        <div className="figma-per-guest-header">
          <h4 className="figma-pricing-title">{t("stepFour.priceRates")}</h4>

          <p className="figma-per-guest-text">
            {t("stepFour.pricingSubtitle")}
          </p>
        </div>

        {/* Horizontal Row 1: Per Day Dropdown + Per Guest Toggle */}
        <div className="figma-row-container">
          <div className="figma-left-half">
            <div className="figma-dropdown-container">
              <select
                className="figma-pricing-select"
                value={pricing.isHourlyEnabled ? "hourly" : "daily"}
                onChange={handlePricingTypeChange}
              >
                <option value="daily">{t("stepFour.perDay")}</option>
                <option value="hourly">{t("stepFour.perHour")}</option>
              </select>
            </div>
          </div>
          {/* empty space */}
          <div className="figma-right-half"></div>
        </div>

        {/* Horizontal Row 2: Price Input + Empty Space */}
        <div className="figma-row-container">
          <div className="figma-left-half">
            <div className="figma-price-section">
              <div className="figma-price-label-container">
                <p className="figma-price-label">{t("stepFour.price")}</p>
              </div>

              <div className="figma-currency-input">
                <span className="figma-currency-symbol">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 14.6484C16.8943 15.5035 16.848 15.8745 16.4531 16.708L10.3896 17.9599C10.529 17.0591 10.7146 16.364 11.0166 15.9472L17 14.6484ZM8.55469 8.75191L10.3662 8.35933V2.63374C11.0413 1.87589 11.4566 1.53558 12.2715 1.10542V7.94527L17 6.9189C16.8943 7.77375 16.8478 8.14428 16.4531 8.97749L12.2715 9.86128V11.7851L17 10.7841C16.8943 11.6392 16.848 12.0101 16.4531 12.8437L12.2715 13.707V13.7246L10.3662 14.1181V10.2636L8.55469 10.6464V13.0761L8.52344 13.082C8.10672 13.8127 7.51827 14.6906 6.95117 15.3916L1 16.5253C1.05336 15.7596 1.16448 15.3279 1.51074 14.5566L6.64941 13.4423V11.0498L1.88672 12.0576C1.94008 11.2919 2.05224 10.861 2.39844 10.0898L6.64941 9.16597V1.56831C7.32454 0.810465 7.73977 0.470151 8.55469 0.0399933V8.75191Z"
                      fill="black"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  name={pricing.isDailyEnabled ? "dailyRate" : "hourlyRate"}
                  value={
                    pricing.isDailyEnabled
                      ? pricing.dailyRate
                      : pricing.hourlyRate
                  }
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className="figma-price-input"
                />
              </div>
            </div>
          </div>

          <div className="figma-right-half">
            <div className="figma-min-guests-section">
              <div className="figma-per-guest-info-text">
                {t("stepFour.perGuestQuestion")}{" "}
                <span className="figma-per-guest-info-text-optional">
                  {t("stepFour.optional")}
                </span>
              </div>
              <div className="figma-per-guest-toggle">
                <label className="figma-radio-container" onClick={handlePerGuestToggle}>
                  <input
                    type="radio"
                    name="pricingType"
                    checked={pricing.perGuest}
                    readOnly
                    className="figma-radio-input"
                  />
                  <span className="figma-radio-custom"></span>
                  <span className="figma-toggle-label">{t("stepFour.perGuest")}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Combined Row: Price Input + Minimum Guests Section */}
        <div className="figma-row-container">
          <div className="figma-left-half">
            <div className="figma-min-guests-section">
              <div className="figma-min-guests-label-container">
                <p className="figma-min-guests-label">
                  {t("stepFour.minimumGuests")}{" "}
                  <span className="figma-min-guests-label-optional">
                    {" "}
                    {t("stepFour.optional")}
                  </span>
                </p>
              </div>

              <div className="figma-min-guests-input-container">
                <input
                  type="text"
                  name="minCapacity"
                  value={pricing.minCapacity}
                  onChange={handleMinCapacityChange}
                  placeholder={t("stepFour.minimumGuestsPlaceholder")}
                  className="figma-min-guests-input"
                />
              </div>
            </div>
          </div>

          <div className="figma-right-half">
            <div className="figma-min-guests-section">
              <p className="figma-min-guests-helper">
                {t("stepFour.minimumGuestsHelper")}
              </p>
            </div>
          </div>
        </div>
        <div className="input">
          <h5>{t("stepFour.inclusionsTitle") || "Inclusions"}</h5>
          <MultiSelectDropdown
            options={inclusionsList.map((inclusion) => {
              const lang = getLangFromLocalStorage();
              const label =
                lang === "ar" && inclusion.name_ar
                  ? inclusion.name_ar
                  : inclusion.name;
              return {
                value: inclusion.id,
                label: label || String(inclusion.id),
              };
            })}
            value={selectedInclusionIds}
            onChange={setSelectedInclusionIds}
            placeholder={
              t("stepFour.inclusionsSubtitle") || "Select inclusions"
            }
          />
        </div>
      </div>

      <div
        className={"Nav"}
        style={{ flexDirection: "column", marginTop: "100px" }}
      >
        <div className={"buttons"}>
          <button
            className={"Cancel"}
            onClick={() => handleNavigateWithParams(4)}
          >
            {t("stepFour.previous") || "Previous"}
          </button>
          <button
            className={"Next"}
            onClick={() => {
              if (status === "loading") return;

              // Convert values to numbers for validation
              const dailyRateNum = parseFloat(pricing.dailyRate);
              const hourlyRateNum = parseFloat(pricing.hourlyRate);

              // Check if daily rate is enabled and validate
              if (pricing.isDailyEnabled) {
                if (isNaN(dailyRateNum) || dailyRateNum <= 0) {
                  alert(t("stepFour.validationDailyRate"));
                  return;
                }
              }

              // Check if hourly rate is enabled and validate
              if (pricing.isHourlyEnabled) {
                if (isNaN(hourlyRateNum) || hourlyRateNum <= 0) {
                  alert(t("stepFour.validationHourlyRate"));
                  return;
                }
              }

              // Ensure at least one rate is enabled
              if (!pricing.isDailyEnabled && !pricing.isHourlyEnabled) {
                alert(t("stepFour.validationPricingOption"));
                return;
              }

              // Validate minimum capacity if per guest is enabled
              if (
                pricing.perGuest &&
                pricing.minCapacity &&
                pricing.minCapacity !== ""
              ) {
                const minCapacityNum = parseInt(pricing.minCapacity);
                if (isNaN(minCapacityNum) || minCapacityNum <= 0) {
                  alert(t("stepFour.validationMinimumGuests"));
                  return;
                }
              }

              if (editVenue)
                update(
                  null,
                  null,
                  null,
                  pricing,
                  id,
                  null,
                  null,
                  selectedInclusionIds
                );
              else createVenue();
            }}
          >
            {editVenue
              ? status === "loading"
                ? t("stepFour.loading")
                : t("stepFour.save")
              : t("stepFour.next")}
          </button>
        </div>
      </div>

      {status === "Success" && (
        <div className={"message_Venue"}>
          <div>
            {status === "Success" && <Success />}
            {t("stepFour.changesSaved")}
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default StepFour;
